import { useTranslation } from "react-i18next"

import type { Project } from "@/services/project-service"

import { Folder, Settings, Share2, Users } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { CATEGORY_TONES } from "@/components/ui/tone-classes"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

type ProjectPageHeaderProps = {
  project: Project | undefined
  onSettingsClick: () => void
  onShareClick: () => void
}

export function ProjectPageHeader({
  project,
  onSettingsClick,
  onShareClick,
}: ProjectPageHeaderProps) {
  const { t } = useTranslation()

  // Show role badge for all roles including owner
  const showRoleBadge = !!project?.currentUserRole
  const roleLabel = (() => {
    if (project?.currentUserRole === "reader") {
      return t("sharing.role_reader", { defaultValue: "Reader" })
    }
    if (project?.currentUserRole === "editor") {
      return t("sharing.role_editor", { defaultValue: "Editor" })
    }
    if (project?.currentUserRole === "admin") {
      return t("sharing.role_admin", { defaultValue: "Admin" })
    }
    if (project?.currentUserRole === "owner") {
      return t("sharing.role_owner", { defaultValue: "Owner" })
    }
    return null
  })()

  // Count other participants (excluding the owner)
  const otherParticipantsCount = (project?.participants?.length ?? 1) - 1
  const isShared = otherParticipantsCount > 0

  return (
    <header className="border-border/40 bg-card/30 supports-[backdrop-filter]:bg-card/20 border-b backdrop-blur">
      <div className="mx-auto max-w-4xl px-3 py-3 sm:px-6 sm:py-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-4">
            <Folder className="size-4 shrink-0 sm:size-5" />
            <h1 className="text-foreground min-w-0 truncate text-base tracking-wider sm:text-xl">
              {project?.name}
            </h1>
            {showRoleBadge && roleLabel && (
              <span
                className={cn(
                  "shrink-0 rounded-full px-2 py-0.5 text-xs font-medium",
                  (() => {
                    const role = project.currentUserRole
                    if (role === "owner") {
                      return "bg-warning/30 text-warning-ink"
                    }
                    if (role === "admin") {
                      return CATEGORY_TONES[1]
                    }
                    if (role === "editor") {
                      return CATEGORY_TONES[0]
                    }
                    return "bg-muted text-muted-foreground border-border border"
                  })()
                )}
              >
                {roleLabel}
              </span>
            )}
            {isShared && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="bg-muted text-muted-foreground flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium">
                      <Users className="size-3" />
                      {otherParticipantsCount + 1}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    {t("sharing.shared_with_count", {
                      count: otherParticipantsCount,
                      defaultValue: "Shared with {{count}} others",
                    })}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    className="border-border/60 hover:bg-accent/50 gap-2 rounded-lg"
                    size="sm"
                    variant="outline"
                    onClick={onShareClick}
                  >
                    <Share2 className="size-4" />
                    <span className="hidden sm:inline">
                      {t("sharing.share", { defaultValue: "Share" })}
                    </span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {t("sharing.share_tooltip", {
                    defaultValue: "Share this project with others",
                  })}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <Button
              className="border-border/60 hover:bg-accent/50 gap-2 rounded-lg"
              size="sm"
              variant="outline"
              onClick={onSettingsClick}
            >
              <Settings className="size-4" />
              <span className="hidden sm:inline">{t("projects.settings")}</span>
            </Button>
          </div>
        </div>
      </div>
    </header>
  )
}
